/**
 * OnboardingModal — Netflix-style two-panel design.
 * Left: cinematic dark sidebar with vertical step navigator.
 * Right: tile-card selections + navigation.
 */
import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  IconButton,
  Autocomplete,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { onboardingApi } from '../services/api';

// ── Step definitions ──────────────────────────────────────────────────────────
const STEPS = [
  { label: 'About You',      emoji: '👋', sub: "Let's start with the basics" },
  { label: 'Your Platforms', emoji: '📱', sub: 'Where do you create content?' },
  { label: 'Content Style',  emoji: '✍️', sub: 'Your voice & communication style' },
  { label: 'Your Goals',     emoji: '🎯', sub: 'What are you working toward?' },
  { label: 'Your Audience',  emoji: '👥', sub: 'Who are you talking to?' },
];

const ROLES     = ['Content Creator', 'Business Owner', 'Agency', 'Social Media Manager', 'Student/Learner'];
const NICHES    = [
  'fitness & wellness', 'productivity', 'personal finance', 'travel photography',
  'food & recipes', 'fashion & beauty', 'technology', 'entrepreneurship',
  'mental health', 'education', 'gaming', 'music', 'art & design',
  'parenting', 'real estate', 'sustainability', 'sports',
];
const PLATFORMS  = ['Instagram', 'YouTube', 'TikTok', 'LinkedIn', 'Twitter', 'Facebook', 'Reddit'];
const TONES      = ['Professional', 'Casual', 'Funny', 'Inspirational', 'Educational', 'Bold/Controversial'];
const EMOJI_USE  = ['Never', 'Occasionally', 'Frequently', 'Every Post'];
const CHALLENGES = ['Coming up with ideas', 'Growing followers', 'Engagement', 'Consistency', 'Monetization'];
const FREQS      = ['Daily', '3–5x/week', '1–2x/week', 'Rarely'];

const INITIAL = {
  role: '', niche: '', platforms: [], primary_platform: '',
  tone: '', emoji_usage: '', challenge: '', posting_frequency: '',
  target_audience: '', audience_topics: [],
};

// ── Per-option emoji + accent colour ─────────────────────────────────────────
const META = {
  'Content Creator':      { emoji: '🎬', color: '#6366f1' },
  'Business Owner':       { emoji: '💼', color: '#10b981' },
  'Agency':               { emoji: '🏢', color: '#f59e0b' },
  'Social Media Manager': { emoji: '📊', color: '#06b6d4' },
  'Student/Learner':      { emoji: '📚', color: '#ec4899' },
  'Instagram': { emoji: '📸', color: '#e1306c' },
  'YouTube':   { emoji: '▶️',  color: '#ff4500' },
  'TikTok':    { emoji: '🎵', color: '#00c8ae' },
  'LinkedIn':  { emoji: '💼', color: '#0a66c2' },
  'Twitter':   { emoji: '🐦', color: '#1d9bf0' },
  'Facebook':  { emoji: '👥', color: '#1877f2' },
  'Reddit':    { emoji: '🤖', color: '#ff6314' },
  'Professional':       { emoji: '👔', color: '#6366f1' },
  'Casual':             { emoji: '😊', color: '#10b981' },
  'Funny':              { emoji: '😂', color: '#f59e0b' },
  'Inspirational':      { emoji: '✨', color: '#ec4899' },
  'Educational':        { emoji: '🎓', color: '#06b6d4' },
  'Bold/Controversial': { emoji: '🔥', color: '#ef4444' },
  'Never':        { emoji: '🚫', color: '#6b7280' },
  'Occasionally': { emoji: '🙂', color: '#10b981' },
  'Frequently':   { emoji: '😄', color: '#f59e0b' },
  'Every Post':   { emoji: '🎉', color: '#ec4899' },
  'Coming up with ideas': { emoji: '💡', color: '#6366f1' },
  'Growing followers':    { emoji: '📈', color: '#10b981' },
  'Engagement':           { emoji: '❤️',  color: '#ef4444' },
  'Consistency':          { emoji: '🎯', color: '#06b6d4' },
  'Monetization':         { emoji: '💰', color: '#f59e0b' },
  'Daily':     { emoji: '🔥', color: '#10b981' },
  '3–5x/week': { emoji: '📅', color: '#6366f1' },
  '1–2x/week': { emoji: '🗓️', color: '#f59e0b' },
  'Rarely':    { emoji: '💤', color: '#6b7280' },
};

// ── Left sidebar — vertical step navigator ───────────────────────────────────
function LeftPanel({ step }) {
  const current = STEPS[step];
  return (
    <Box
      sx={{
        width: 220,
        flexShrink: 0,
        background: 'linear-gradient(165deg, #0a0a18 0%, #12122a 60%, #0f172a 100%)',
        borderRight: '1px solid rgba(99,102,241,0.12)',
        display: 'flex',
        flexDirection: 'column',
        p: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '15%', left: '-30%',
          width: '80%', height: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.13), transparent 70%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '8%', right: '-20%',
          width: '60%', height: '40%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.09), transparent 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      {/* Brand mark */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4, zIndex: 1 }}>
        <Box
          sx={{
            width: 32, height: 32, borderRadius: '8px',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1rem',
            boxShadow: '0 0 14px rgba(99,102,241,0.55)',
          }}
        >
          ✨
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#fff', letterSpacing: -0.3 }}>
          Setup Wizard
        </Typography>
      </Box>

      {/* Step list */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25, zIndex: 1 }}>
        {STEPS.map((s, i) => {
          const isActive = i === step;
          const isDone   = i < step;
          return (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
              <Box
                sx={{
                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isActive
                    ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                    : isDone ? 'rgba(99,102,241,0.22)' : 'rgba(255,255,255,0.04)',
                  border: `1.5px solid ${isActive ? '#6366f1' : isDone ? 'rgba(99,102,241,0.35)' : 'rgba(255,255,255,0.1)'}`,
                  boxShadow: isActive ? '0 0 10px rgba(99,102,241,0.5)' : 'none',
                  transition: 'all 0.3s ease',
                }}
              >
                {isDone
                  ? <CheckIcon sx={{ fontSize: 12, color: 'rgba(139,92,246,0.9)' }} />
                  : (
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, lineHeight: 1, color: isActive ? '#fff' : 'rgba(255,255,255,0.22)' }}>
                      {i + 1}
                    </Typography>
                  )}
              </Box>
              <Box>
                <Typography sx={{
                  fontSize: '0.78rem',
                  fontWeight: isActive ? 700 : 400,
                  color: isActive ? '#fff' : isDone ? 'rgba(255,255,255,0.42)' : 'rgba(255,255,255,0.2)',
                  transition: 'all 0.3s ease',
                  lineHeight: 1.2,
                }}>
                  {s.label}
                </Typography>
                {isActive && (
                  <Typography sx={{ fontSize: '0.62rem', color: 'rgba(99,102,241,0.75)', fontWeight: 500, mt: 0.25 }}>
                    In progress
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Current step hero */}
      <Box sx={{ mt: 3, zIndex: 1 }}>
        <Typography sx={{ fontSize: '2.8rem', lineHeight: 1, mb: 1 }}>
          {current.emoji}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.32)', lineHeight: 1.5 }}>
          {current.sub}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Netflix tile card selector ────────────────────────────────────────────────
function TileSelector({ options, value, onChange, multi = false }) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const selected = multi ? (value || []) : value;

  const handleClick = (opt) => {
    if (multi) {
      onChange(selected.includes(opt) ? selected.filter((v) => v !== opt) : [...selected, opt]);
    } else {
      onChange(opt === selected ? '' : opt);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mt: 1.5 }}>
      {options.map((opt) => {
        const active = multi ? selected.includes(opt) : selected === opt;
        const { emoji = '●', color = '#6366f1' } = META[opt] || {};
        return (
          <Box
            key={opt}
            role="button"
            tabIndex={0}
            onClick={() => handleClick(opt)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleClick(opt)}
            aria-pressed={active}
            sx={{
              cursor: 'pointer',
              minWidth: { xs: 82, sm: 98 },
              borderRadius: '12px',
              pt: 1.5, pb: 1.25, px: 1.25,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 0.75,
              border: `1.5px solid ${active ? color : alpha(isDark ? '#fff' : '#000', 0.09)}`,
              background: active
                ? `linear-gradient(145deg, ${alpha(color, isDark ? 0.28 : 0.14)} 0%, ${alpha(color, isDark ? 0.1 : 0.05)} 100%)`
                : isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              boxShadow: active
                ? `0 0 0 1px ${alpha(color, 0.25)}, 0 8px 24px ${alpha(color, isDark ? 0.3 : 0.18)}`
                : 'none',
              transform: active ? 'scale(1.05)' : 'scale(1)',
              transition: 'all 0.2s cubic-bezier(0.34,1.56,0.64,1)',
              position: 'relative',
              overflow: 'hidden',
              userSelect: 'none',
              // top accent bar
              '&::before': {
                content: '""',
                position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                background: active
                  ? `linear-gradient(90deg, ${color}, ${alpha(color, 0.35)})`
                  : 'transparent',
                transition: 'background 0.2s ease',
                borderRadius: '12px 12px 0 0',
              },
              '&:hover': {
                transform: active ? 'scale(1.07)' : 'scale(1.04)',
                border: `1.5px solid ${active ? color : alpha(color, 0.4)}`,
                boxShadow: `0 8px 28px ${alpha(color, isDark ? 0.32 : 0.2)}`,
              },
            }}
          >
            <Typography sx={{ fontSize: '1.65rem', lineHeight: 1 }}>{emoji}</Typography>
            <Typography sx={{
              fontSize: '0.72rem',
              fontWeight: active ? 700 : 500,
              color: active
                ? isDark ? alpha(color, 0.95) : color
                : isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
              textAlign: 'center',
              lineHeight: 1.3,
              transition: 'color 0.2s ease',
            }}>
              {opt}
            </Typography>

            {active && (
              <Box sx={{
                position: 'absolute', top: 7, right: 7,
                width: 16, height: 16, borderRadius: '50%',
                bgcolor: color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 0 6px ${alpha(color, 0.6)}`,
              }}>
                <CheckIcon sx={{ fontSize: 10, color: '#fff' }} />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ── Question block ────────────────────────────────────────────────────────────
function Question({ icon, label, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
        {icon && <Typography sx={{ fontSize: '1.05rem', lineHeight: 1 }}>{icon}</Typography>}
        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.92rem' }}>
          {label}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

// ── Step content ──────────────────────────────────────────────────────────────
function StepContent({ step, data, set, topicInput, setTopicInput, addTopic, removeTopic }) {
  const theme = useTheme();
  switch (step) {
    case 0:
      return (
        <>
          <Question icon="💼" label="What best describes you?">
            <TileSelector options={ROLES} value={data.role} onChange={(v) => set('role', v)} />
          </Question>
          <Question icon="🔍" label="What's your content niche?">
            <Autocomplete
              freeSolo options={NICHES} value={data.niche}
              onInputChange={(_, v) => set('niche', v)}
              renderInput={(params) => (
                <TextField {...params} size="small"
                  placeholder="e.g. fitness & wellness, SaaS marketing…"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: alpha(theme.palette.background.default, 0.5) } }}
                />
              )}
            />
          </Question>
        </>
      );
    case 1:
      return (
        <>
          <Question icon="📲" label="Which platforms do you use?">
            <TileSelector options={PLATFORMS} value={data.platforms} onChange={(v) => set('platforms', v)} multi />
          </Question>
          {data.platforms.length > 0 && (
            <Question icon="⭐" label="Which is your primary platform?">
              <TileSelector options={data.platforms} value={data.primary_platform} onChange={(v) => set('primary_platform', v)} />
            </Question>
          )}
        </>
      );
    case 2:
      return (
        <>
          <Question icon="🎭" label="How would you describe your tone?">
            <TileSelector options={TONES} value={data.tone} onChange={(v) => set('tone', v)} />
          </Question>
          <Question icon="😄" label="How often do you use emoji?">
            <TileSelector options={EMOJI_USE} value={data.emoji_usage} onChange={(v) => set('emoji_usage', v)} />
          </Question>
        </>
      );
    case 3:
      return (
        <>
          <Question icon="🚧" label="What's your biggest challenge?">
            <TileSelector options={CHALLENGES} value={data.challenge} onChange={(v) => set('challenge', v)} />
          </Question>
          <Question icon="📅" label="How often do you post?">
            <TileSelector options={FREQS} value={data.posting_frequency} onChange={(v) => set('posting_frequency', v)} />
          </Question>
        </>
      );
    case 4:
      return (
        <>
          <Question icon="🧑‍🤝‍🧑" label="Who is your target audience?">
            <TextField size="small" fullWidth
              placeholder="e.g. busy professionals aged 25–40"
              value={data.target_audience}
              onChange={(e) => set('target_audience', e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: alpha(theme.palette.background.default, 0.5) } }}
            />
          </Question>
          <Question icon="💬" label="What topics does your audience care about?">
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small"
                placeholder="Add a topic…"
                value={topicInput}
                onChange={(e) => setTopicInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTopic())}
                sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: alpha(theme.palette.background.default, 0.5) } }}
              />
              <Button variant="outlined" size="small" onClick={addTopic}
                sx={{ borderRadius: 2, minWidth: 56, whiteSpace: 'nowrap' }}>
                Add
              </Button>
            </Box>
            {data.audience_topics.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
                {data.audience_topics.map((t) => (
                  <Chip key={t} label={t} size="small" onDelete={() => removeTopic(t)}
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main', border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`, fontWeight: 600 }}
                  />
                ))}
              </Box>
            )}
          </Question>
        </>
      );
    default:
      return null;
  }
}

// ── Main component ────────────────────────────────────────────────────────────
export default function OnboardingModal({ open, onComplete, onSkip }) {
  const theme    = useTheme();
  const isDark   = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [step, setStep]             = useState(0);
  const [data, setData]             = useState(INITIAL);
  const [topicInput, setTopicInput] = useState('');
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState('');

  const set        = (field, val) => setData((prev) => ({ ...prev, [field]: val }));
  const handleNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleSkip = async () => {
    try { await onboardingApi.save(data, false); } catch (_) {}
    onSkip?.();
  };

  const handleComplete = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await onboardingApi.save(data, true);
      onComplete?.(data);
    } catch (err) {
      console.error('Onboarding save failed:', err);
      setSaveError('Failed to save — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addTopic    = () => {
    const t = topicInput.trim().toLowerCase();
    if (t && !data.audience_topics.includes(t)) set('audience_topics', [...data.audience_topics, t]);
    setTopicInput('');
  };
  const removeTopic = (t) => set('audience_topics', data.audience_topics.filter((x) => x !== t));

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '10px',
          overflow: 'hidden',
          background: 'transparent',
          border: '1px solid rgba(99,102,241,0.18)',
          boxShadow: '0 40px 80px -12px rgba(0,0,0,0.7), 0 0 0 1px rgba(99,102,241,0.1)',
          display: 'flex',
          flexDirection: 'row',
          minHeight: 500,
          mx: { xs: 2, sm: 4 },          // side margins on mobile
          width: { xs: 'calc(100% - 32px)', sm: undefined },
        },
      }}
    >
      {/* Left sidebar — hidden on mobile */}
      {!isMobile && <LeftPanel step={step} />}

      {/* Right panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: isDark
            ? 'linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)'
            : theme.palette.background.paper,
          minWidth: 0,
        }}
      >
        {/* Top bar */}
        <Box sx={{
          px: 3, pt: 2.5, pb: 2,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
        }}>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.1, letterSpacing: -0.3, fontSize: '1.05rem' }}>
              {STEPS[step].label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
              Step {step + 1} of {STEPS.length}
            </Typography>
          </Box>

          {/* Progress dots */}
          <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center' }}>
            {STEPS.map((_, i) => (
              <Box key={i} sx={{
                width: i === step ? 18 : 7, height: 7,
                borderRadius: 99,
                transition: 'all 0.3s ease',
                background: i < step
                  ? alpha(theme.palette.primary.main, 0.4)
                  : i === step
                  ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
                  : alpha(theme.palette.divider, 0.4),
              }} />
            ))}
          </Box>

          <IconButton size="small" onClick={handleSkip}
            sx={{ color: 'text.secondary', ml: 1, bgcolor: alpha(theme.palette.background.paper, 0.3), '&:hover': { bgcolor: alpha(theme.palette.background.paper, 0.6) } }}>
            ✕
          </IconButton>
        </Box>

        {/* Progress bar */}
        <Box sx={{ height: 3, bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
          <Box
            role="progressbar"
            aria-valuenow={Math.round(((step + 1) / STEPS.length) * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            sx={{
              height: '100%',
              width: `${((step + 1) / STEPS.length) * 100}%`,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              transition: 'width 0.4s ease',
              boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}`,
            }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, px: 3, pt: 3, pb: 1, overflowY: 'auto', maxHeight: { xs: '55vh', sm: 460 } }}>
          <StepContent
            step={step} data={data} set={set}
            topicInput={topicInput} setTopicInput={setTopicInput}
            addTopic={addTopic} removeTopic={removeTopic}
          />
          {saveError && (
            <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600, mb: 1 }}>
              {saveError}
            </Typography>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{
          px: 3, py: 2,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          bgcolor: alpha(theme.palette.background.default, 0.4),
        }}>
          <Button size="small" onClick={handleSkip}
            sx={{ color: 'text.disabled', fontSize: '0.78rem', textDecoration: 'underline', textUnderlineOffset: 3, '&:hover': { color: 'text.secondary', bgcolor: 'transparent' } }}>
            Skip for now
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {step > 0 && (
              <Button variant="outlined" size="small" onClick={handleBack}
                sx={{ borderRadius: 2, px: 2, borderColor: alpha(theme.palette.divider, 0.6), color: 'text.secondary', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}>
                ← Back
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button variant="contained" size="small" onClick={handleNext}
                sx={{
                  borderRadius: 2, px: 3, fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.45)}`,
                  '&:hover': { boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.55)}`, transform: 'translateY(-1px)' },
                }}>
                Next →
              </Button>
            ) : (
              <Button variant="contained" size="small" onClick={handleComplete} disabled={saving}
                sx={{
                  borderRadius: 2, px: 3, fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.45)}`,
                  '&:hover': { boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.55)}`, transform: 'translateY(-1px)' },
                  '&.Mui-disabled': { opacity: 0.6 },
                }}>
                {saving ? 'Saving…' : '✨ Get Started'}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
